import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import Box from '@components/base/Box'
import LoadingSpinner from '@components/base/LoadingSpinner'
import {
  Event,
  EventAttendee,
  RegistrationField,
} from '@graphql/__generated/graphql'
import DefaultThumbnail from '@public/images/user-icon.png'
import { ATTENDEE_BOX_GET_USERS } from '@graphql/components/AttendeeBox.gql'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useAuth } from '@libs/useAuth'

type EventAttendeeWithRender = EventAttendee & {
  resentMessage?: string
  registrationFields?: Record<string, string>
}

export const AttendeeBox = ({
  eventId,
  event,
  show = true,
}: {
  show?: boolean
  eventId: string
  event: {
    attendees: EventAttendeeWithRender[]
    registrationFields: RegistrationField[]
    organisationName: string
  }
}) => {
  const { isOrganizer, isAdmin } = useAuth()

  const registrationFields = (event?.registrationFields ?? []).filter(
    (f) => !!f && !!f.name
  )

  const attendees = event.attendees as EventAttendeeWithRender[]

  const [userRegistrationFields, setUserRegistrationFields] =
    useState<{ id: string; registrationFields: any }[]>()

  const { data, loading: regFieldsLoading } = useQuery(ATTENDEE_BOX_GET_USERS, {
    variables: {
      input: {
        eventId,
      },
    },
  })

  const [localAttendees, setLocalAttendees] =
    useState<EventAttendeeWithRender[]>(attendees)

  const SPECIAL_CASE_OF_EXTERNAL_ONLY_APPLY_FOR_EVENTS = [
    '59080128-59d6-4164-a824-0e48b01ef75f',
    '2af2f67c-57aa-4b6a-b89e-dc46c3a1333a',
    'a37735e2-95e0-47ee-b5e9-1ce852c27cae', // for staging testing
  ]

  useEffect(() => {
    console.log({
      data,
    })
    if (data) {
      const currentAttendess = attendees.map((at) => at.email ?? at.userId)

      const attendessMetadata = data.getRegistrationFieldAnswersByEventId.items
        .filter((item) => {
          const cond =
            (currentAttendess.includes(item?.email) ||
              (!!item.userId && currentAttendess.includes(item?.userId))) &&
            // @ts-ignore
            Object.entries(item?.registrationFields ?? {}).length > 0

          return cond
        })
        .map((fd) => ({
          ...fd,
          registrationFields:
            fd.registrationFields?.[event.organisationName] ??
            fd.registrationFields,
        }))
      console.log({ currentAttendess, attendessMetadata })
      const registrationFields = currentAttendess.map((at) => {
        const payload = {
          id: at,
          registrationFields:
            attendessMetadata.filter(
              (item) => {
                return item?.email === at || item?.userId === at
              }

              // @ts-ignore
            )?.[0]?.registrationFields ?? {},
        }
        return payload
      })
      console.log({ registrationFields })
      setUserRegistrationFields(registrationFields)
    }
  }, [data])

  useEffect(() => {
    if (
      !regFieldsLoading &&
      !!userRegistrationFields &&
      userRegistrationFields.length > 0
    ) {
      const updatedAttendees = attendees.map((attendee) => {
        const matchedUser = userRegistrationFields.find(
          (user) => user.id === (attendee.email ?? attendee.userId)
        )!

        if (!matchedUser?.registrationFields) return attendee
        const visibleFields = Object.entries(
          matchedUser?.registrationFields!
        ).reduce(
          (acc, [key, value]) => {
            const fieldDef = registrationFields.find(
              (f) => f.name === key && f.visibleOnAttendeeList
            )
            if (fieldDef) acc[key] = value as string
            return acc
          },
          {} as Record<string, string>
        )

        return { ...attendee, registrationFields: visibleFields }
      })

      setLocalAttendees(updatedAttendees)
    }
  }, [userRegistrationFields])

  if (!show || (!regFieldsLoading && localAttendees?.length === 0)) {
    return <></>
  }

  return (
    <Box
      color="foregroundColour"
      className="w-full p-10 max-w-md border border-forumm-menu-border rounded-2xl flex flex-col flex-1"
    >
      <h2 className="text-2xl font-medium">Attendees</h2>
      <Box className="mb-4">
        <span className="text-sm text-gray-600">
          Total Registered: {event.attendees?.length}
        </span>
      </Box>
      {regFieldsLoading && (
        <Box className="w-full flex justify-center">
          <LoadingSpinner size="small" />
        </Box>
      )}
      {/* Added max height and scroll */}
      <Box className="space-y-4 max-h-[100vh] overflow-y-auto scrollbar-custom flex-1 h-[100%]">
        {localAttendees
          .filter(
            (attendee) =>
              attendee.registrationVisible === true ||
              event.organisationName.toLowerCase() === 'university of glasgow'
          )
          .sort((a, b) => {
            if (!a.name || !b.name) return 0
            return a.name.localeCompare(b.name)
          })
          .map((attendee, index) => (
            <Box
              key={index}
              className="flex flex-col border-b pb-6 last:border-b-0"
            >
              <Box className="flex items-center space-x-4">
                {/* <Image */}
                {/*   src={attendee?.profileImage ?? DefaultThumbnail} */}
                {/*   alt={attendee.name ?? 'attendee'} */}
                {/*   width={40} */}
                {/*   height={40} */}
                {/*   className="rounded-full border p-1" */}
                {/* /> */}
                <Box className="flex-1">
                  <p className="font-medium mt-4 text-lg ml-2">
                    {attendee.name}
                  </p>
                </Box>
              </Box>
              {attendee.registrationFields && (
                <Box className="-mt-4 text-sm text-gray-60">
                  {Object.entries(attendee.registrationFields).map(
                    ([key, value]) => (
                      <Box key={key} className="flex space-x-2">
                        <span className="text-sm text-gray-600 capitalize">
                          {/* {key}: */}
                        </span>
                        <span className="text-sm text-gray-600 capitalize">
                          {value}
                        </span>
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  )
}

export default AttendeeBox
